import Dialog from "@mui/material/Dialog";
import { styled as muiStyled } from "@mui/material/styles";
import React, { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { OngBaseError } from "../../api/ong-base-response";
import { useAppDispatch } from "../../app/hooks";
import {
  endGlobalLoadingAction,
  startGlobalLoadingAction,
} from "../../app/reducers/loading-slice";
import { useCreateDeliveryBulkMutation } from "../../query/delivery-query";
import theme from "../../style/theme";
import { Confirm } from "../../types/confirm";
import { CreateDeliveryPayload } from "../../types/delivery/delivery-payload";
import { CheckedExcelData } from "../../types/excel/excel-data";
import { HelperText } from "../../types/helper-text";
import { DeliveryGroupType } from "../../types/schedule/delivery-group-type";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { deliveryErrorCheck } from "../../utils/excel/delivery-error-check";
import { fileToExcelData } from "../../utils/excel/excel-data";
import { ContainedYellowButton } from "../button/ContainedYellowButton";
import { TextWhiteButton } from "../button/TextWhiteButton";
import { DeliveryExcelFileDropZone } from "../dropzone/DeliveryExcelFileDropZone";
import CloseSmallLineIcon from "../icons/CloseSmallLineIcon/CloseSmallLineIcon";

interface Props {
  handleClickExcelDialogClose: () => void;
}

export const DeliveryExcelUploadDialog: React.FC<Props> = ({
  handleClickExcelDialogClose,
}) => {
  const dispatch = useAppDispatch();
  const [checkedExcelData, setCheckedExcelData] = useState<CheckedExcelData[]>(
    []
  );
  const [excelRequestData, setExcelRequestData] = useState<
    CreateDeliveryPayload[]
  >([]);
  const [apiResponseDataStore, setApiResponseDataStore] = useState<HelperText>(
    {}
  );
  const [progress, setProgress] = useState<number>(0);
  const createDeliveryBulkMutation = useCreateDeliveryBulkMutation();

  const findDuplicatedExcelData = (newExcelData: any[]) => {
    return checkedExcelData.find((checkedData) =>
      newExcelData.some(
        (newData: any) => newData.file.name === checkedData.name
      )
    );
  };

  const setFiles = async (files: File[]) => {
    const newExcelData = files.map((file) => fileToExcelData(file));
    const duplicatedExcelData = findDuplicatedExcelData(newExcelData);

    if (duplicatedExcelData) {
      toast.error(`${duplicatedExcelData.name} 파일이 중복되었습니다.`);
      return;
    }

    setProgressNumber(30, files);
    const checkedExcelDataEndRowIndex = checkedExcelData.at(-1)?.endRowIndex;
    const rowIndex = checkedExcelDataEndRowIndex
      ? checkedExcelDataEndRowIndex + 1
      : 1;

    const { checkExcelSheetData, createDeliveryRequestData } =
      await deliveryErrorCheck(newExcelData, apiResponseDataStore, rowIndex);
    setCheckedExcelData(
      checkedExcelData.concat(
        checkExcelSheetData.filter((data) => !checkedExcelData.includes(data))
      )
    );

    setProgressNumber(60, files);
    setExcelRequestData(
      excelRequestData.concat(
        createDeliveryRequestData.filter(
          (data) => !excelRequestData.includes(data)
        )
      )
    );

    setProgressNumber(90, files);
    setTimeout(() => {
      setProgressNumber(100, files);
    }, 500);
  };

  const setProgressNumber = (number: number, files: File[]) => {
    if (files.length > 0) {
      setProgress(number);
    }
  };

  const setRemoveFiles = async (index: number) => {
    const filteredCheckedExcelData = checkedExcelData
      .filter((data, idx) => idx !== index)
      .map((data) => data.excelData);

    const checkedExcelDataEndRowIndex = checkedExcelData.at(-1)?.endRowIndex;
    const rowIndex = checkedExcelDataEndRowIndex
      ? checkedExcelDataEndRowIndex + 1
      : 1;

    const { checkExcelSheetData, createDeliveryRequestData } =
      await deliveryErrorCheck(
        filteredCheckedExcelData,
        apiResponseDataStore,
        rowIndex
      );

    setCheckedExcelData(checkExcelSheetData);
    setExcelRequestData(createDeliveryRequestData);
  };

  const handleClickCreateButton = async () => {
    try {
      if (excelRequestData.length == 0) {
        toast.error(Confirm.Error);
      } else {
        dispatch(startGlobalLoadingAction());
        createDeliveryBulkMutation.mutate(excelRequestData, {
          onSuccess: () => {
            initData();
            dispatch(endGlobalLoadingAction());
            handleClickExcelDialogClose();
          },
          onError: (error: any) => {
            const baseError = error.response.data.error;
            if (baseError.detail.type === "DUPLICATED") {
              handleDuplicatedError(baseError);
            }
            if (
              [DeliveryGroupType.PickUp, DeliveryGroupType.DropOff].includes(
                baseError.detail.type
              )
            ) {
              handleAddressError(baseError);
            }
            toast.error(Confirm.Error);
            dispatch(endGlobalLoadingAction());
          },
        });
      }
    } catch (error) {
      dispatch(endGlobalLoadingAction());
    }
  };

  const handleAddressError = (error: OngBaseError) => {
    const errorRowIndex = Number(error.detail.index) + 1;
    const errorColumn = error.detail.type === "PICK_UP" ? "D" : "K";

    const newCheckedExcelData = checkedExcelData.map((data) => {
      if (
        data.startRowIndex <= errorRowIndex &&
        errorRowIndex <= data.endRowIndex
      ) {
        return {
          ...data,
          error: true,
          rowError: [
            ...data.rowError,
            `[${errorRowIndex - data.startRowIndex + 4}행 ${errorColumn}열] ${error.detail.reason
            }`,
          ],
        };
      } else {
        return data;
      }
    });
    setCheckedExcelData(newCheckedExcelData);
  };

  const handleDuplicatedError = (error: OngBaseError) => {
    console.log(error);
    const errorRowIndex = Number(error.detail.index) + 1;

    const newCheckedExcelData = checkedExcelData.map((data) => {
      if (
        data.startRowIndex <= errorRowIndex &&
        errorRowIndex <= data.endRowIndex
      ) {
        return {
          ...data,
          error: true,
          rowError: [
            ...data.rowError,
            `[${errorRowIndex - data.startRowIndex + 4}행] ${error.detail.reason
            }`,
          ],
        };
      } else {
        return data;
      }
    });
    setCheckedExcelData(newCheckedExcelData);
  };

  const initData = () => {
    setCheckedExcelData([]);
    setExcelRequestData([]);
  };

  useDidMountEffect(() => {
    if (progress === 100) {
      setProgress(0);
    }
  }, [progress]);

  return (
    <BootstrapDialog
      onClose={handleClickExcelDialogClose}
      aria-labelledby="customized-dialog-title"
      open
    >
      <ExcelUploadDialogContainer>
        <TitleContainer>
          <TitleLeftContainer>파일로 등록</TitleLeftContainer>
          <TitleRightContianer onClick={handleClickExcelDialogClose}>
            <CloseSmallLineIcon
              width="1.5rem"
              height="1.5rem"
              fill={theme.palette.primitives.gray100}
            />
          </TitleRightContianer>
        </TitleContainer>
        <BodyContainer>
          <DeliveryExcelFileDropZone
            progress={progress}
            checkedExcelData={checkedExcelData}
            setFiles={setFiles}
            setRemoveFiles={setRemoveFiles}
          />
        </BodyContainer>
        <FooterContainer>
          <TextWhiteButton
            width="114px"
            height="51px"
            text="초기화"
            onClick={initData}
          />
          <CreateButtonContainer>
            <ContainedYellowButton
              width="258px"
              height="51px"
              text="등록하기"
              onClick={handleClickCreateButton}
              disabled={
                createDeliveryBulkMutation.isLoading ||
                checkedExcelData.some((data) => data.error)
              }
            />
          </CreateButtonContainer>
        </FooterContainer>
      </ExcelUploadDialogContainer>
    </BootstrapDialog>
  );
};

const ExcelUploadDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 436px;
  border-radius: 0.5rem;
`;

const TitleContainer = styled.div`
  height: 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${theme.palette.primitives.black};
`;

const TitleLeftContainer = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray50};
  margin-left: 1.5rem;
`;

const TitleRightContianer = styled.div`
  margin-right: 0.438rem;
  cursor: pointer;
`;

const BodyContainer = styled.div`
  overflow-y: auto;
  padding 20px 24px;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding 0 24px 20px;
`;

const CreateButtonContainer = styled.div`
  margin-left: 16px;
`;

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));
